import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Check, CreditCard, Phone, Smartphone, Tag, Trash, X } from 'lucide-react'
import { RotatingLines, TailSpin } from 'react-loader-spinner'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { MatchedDoctor } from '../../types/User'
import { AppState } from '../../state'
import { useApplyPromo, useRequestConsultation } from '../../state/features/authSlice/hooks'
import { useSwitchPage } from '../../state/features/applicationSlice/hooks'
import { PromoCode } from '../../types'
import { getUserData } from '../../functions/firebase'
import { setUser } from '../../state/features/authSlice'
import { BookingSource } from '../../types/Consultation'
import { analytics } from '../../functions/analytics'
import { ApplicationPages, setActiveBooking } from '../../state/features/applicationSlice'
import { DDGColors } from '../../constants/colors'
import Image from '../Image'
import { convertDateToString } from '../../functions'
import { db } from '../../config/firebase'
import { doc, onSnapshot } from 'firebase/firestore'
import { consultationsCollection, appointmentsCollections } from '../../constants/firebase'
import { axiosClient } from '../../config/axiosClient'

type SelectedMethod = {
	id: string
	type: 'card' | 'mobile_money' | 'WALLET'
	value?: string
} | null

const MOMO_PROVIDERS = [
	{ id: 'mtn', label: 'MTN MoMo', logo: 'https://www.mtn.com/wp-content/uploads/2022/12/momo-logo-horiz.png' },
	{ id: 'atl', label: 'AirtelTigo', logo: 'https://amaghanaonline.com/wp-content/uploads/2022/07/WhatsApp-Image-2022-07-27-at-5.16.26-PM.jpeg' },
	{ id: 'vod', label: 'Telecel Cash', logo: 'https://play.telecel.com.gh/static/Rede-5f0f780acc6c05a6539d7e3229ac508c.webp' },
	{ id: 'gmoney', label: 'G-Money', logo: 'https://gm-shuttle.gmfs.africa/web_app/assets/brand/gmoney_light.png' },
]

const PENDING_BOOKING_KEY = 'ddg_widget_pending_booking'

interface StepThreeProps {
	activeDoctor: MatchedDoctor
	callbackFunc: () => void
}

const StepThree: React.FC<StepThreeProps> = ({ activeDoctor, callbackFunc }) => {
	const { User, Account, PaymentMethods } = useSelector<AppState, AppState['auth']>((s) => s.auth)
	const { activeBooking, activeFacility } = useSelector<AppState, AppState['application']>((s) => s.application)
	const applyPromo = useApplyPromo()
	const requestConsultation = useRequestConsultation()
	const dispatch = useDispatch()
	const router = useSwitchPage()

	const [selectedMethod, setSelectedMethod] = useState<SelectedMethod>(null)
	const [momoPhone, setMomoPhone] = useState('')
	const [promoCodeData, setPromoCodeData] = useState<PromoCode>()
	const [promoCode, setPromoCode] = useState<string>()
	const [loading, setLoading] = useState(false)
	const [applying, setApplying] = useState(false)
	const [savedCardsOpen, setSavedCardsOpen] = useState(false)

	// Payment flow state
	const [pendingBooking, setPendingBooking] = useState<{ cid: string; aid: string } | null>(null)
	const [awaitingPayment, setAwaitingPayment] = useState(false)
	const [payReference, setPayReference] = useState<string | null>(null)
	const [paymentMessage, setPaymentMessage] = useState('')
	const [pendingPrId, setPendingPrId] = useState<string | null>(null)
	const [manualVerifying, setManualVerifying] = useState(false)
	const [timeLeft, setTimeLeft] = useState(300)
	const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

	const bookingTrackedRef = useRef(false)
	useEffect(() => {
		if (bookingTrackedRef.current) return
		bookingTrackedRef.current = true
		analytics.trackEvent('booking_initiated', {
			facility_id: activeFacility?.id ?? '',
			source: 'BOOKING_PAGE',
		})
	}, [])

	// OTP state
	const [showOtp, setShowOtp] = useState(false)
	const [otpInput, setOtpInput] = useState('')
	const [submittingOtp, setSubmittingOtp] = useState(false)

	const appointmentCost = useMemo(() => {
		const providerPrice = activeDoctor?.enabledServices[activeBooking.visitType.value || 'VIRTUAL'].price
		const studentDiscountPrice = activeDoctor.accepts_students_package && User?.is_student ? providerPrice * 0.7 : 0
		const promoDiscountPrice = promoCodeData ? promoCodeData.value * providerPrice : 0
		return {
			providerPrice,
			discountedPrice: promoCodeData ? promoDiscountPrice : studentDiscountPrice,
		}
	}, [promoCodeData, User])

	const hasFreePerks = (Account?.perks?.consultation?.[activeBooking.visitType?.value] ?? 0) > 0 && activeDoctor?.accepts_perks
	const walletBalance = (Account?.balance?.value ?? 0) + (Account?.balance?.org_value ?? 0)
	const effectiveTotal = appointmentCost.discountedPrice || appointmentCost.providerPrice || 0
	const walletDeduction = hasFreePerks ? 0 : Math.min(walletBalance, effectiveTotal)
	const paymentRemainder = hasFreePerks ? 0 : Math.max(0, effectiveTotal - walletDeduction)

	const fetchData = async () => {
		const userAccount = await getUserData(Account?.uid ?? '')
		dispatch(setUser(userAccount))
	}

	const navigateToSuccess = (cid: string, aid: string) => {
		fetchData()
		const urlParams = new URLSearchParams(window.location.search)
		urlParams.set('time', activeBooking.startTime?.toString() ?? '0')
		urlParams.set('type', activeBooking.visitType.value)
		urlParams.set('cid', cid)
		urlParams.set('aid', aid)
		window.history.pushState(null, '', '?' + urlParams.toString())
		router(ApplicationPages.SUCCESS)
		dispatch(setActiveBooking({ visitType: { name: 'Video/Virtual visit', value: 'VIRTUAL' }, complaints: '', startTime: null, activeInterval: null }))
	}

	// Firestore listener — fires when payment webhook confirms the appointment
	useEffect(() => {
		if (!pendingBooking || !awaitingPayment) return
		const ref = doc(db, consultationsCollection, pendingBooking.cid, appointmentsCollections, pendingBooking.aid)
		const unsub = onSnapshot(ref, (snap) => {
			if (!snap.exists()) return
			const appt = snap.data() as any
			if (appt.status === 'ACCEPTED' && appt.paymentStatus === 'PAID') {
				if (timerRef.current) clearInterval(timerRef.current)
				setAwaitingPayment(false)
				setLoading(false)
				sessionStorage.removeItem(PENDING_BOOKING_KEY)
				navigateToSuccess(pendingBooking.cid, pendingBooking.aid)
			} else if (appt.status === 'CANCELED') {
				if (timerRef.current) clearInterval(timerRef.current)
				setAwaitingPayment(false)
				setLoading(false)
				toast.error('Your payment was received but the slot became unavailable. Funds have been credited to your wallet.')
			}
		})
		return () => { unsub(); if (timerRef.current) clearInterval(timerRef.current) }
	}, [pendingBooking, awaitingPayment])

	// 5-minute countdown when manually verifying
	useEffect(() => {
		if (!manualVerifying) return
		timerRef.current = setInterval(() => {
			setTimeLeft((s) => {
				if (s <= 1) {
					clearInterval(timerRef.current!)
					setManualVerifying(false)
					setAwaitingPayment(false)
					setLoading(false)
					toast.error('Payment confirmation timed out. Contact support if payment was deducted.')
					return 0
				}
				return s - 1
			})
		}, 1000)
		return () => { if (timerRef.current) clearInterval(timerRef.current) }
	}, [manualVerifying])

	// Handle return from card payment redirect
	useEffect(() => {
		const trxref = new URLSearchParams(window.location.search).get('trxref')
		if (!trxref) return
		const pending = sessionStorage.getItem(PENDING_BOOKING_KEY)
		if (!pending) return
		try {
			const { cid, aid } = JSON.parse(pending)
			setPendingBooking({ cid, aid })
			setManualVerifying(true)
			setTimeLeft(300)
			setAwaitingPayment(true)
		} catch {}
	}, [])

	const formatTimer = (s: number) => {
		const m = Math.floor(s / 60)
		const sec = s % 60
		return `${m}:${sec < 10 ? '0' : ''}${sec}`
	}

	const startPaymentForRequest = async (prId: string, cid: string, aid: string) => {
		setPendingPrId(prId)
		const isMomo = selectedMethod?.type === 'mobile_money'
		try {
			if (isMomo) {
				const { data } = await axiosClient.post('/payalt/init-payment-request', {
					paymentRequestId: prId,
					channel: 'mobile_money',
					phone: selectedMethod!.value,
					provider: selectedMethod!.id,
				})
				const { status, reference, display_text } = data?.data ?? {}
				if (reference) setPayReference(reference)
				setLoading(false)
				if (status === 'send_otp') {
					setShowOtp(true)
					setAwaitingPayment(true)
				} else {
					setPaymentMessage(display_text ?? 'Please complete the payment on your mobile device')
					setAwaitingPayment(true)
				}
			} else {
				const { data } = await axiosClient.post('/payalt/init-payment-request', {
					paymentRequestId: prId,
					channel: 'card',
				})
				sessionStorage.setItem(PENDING_BOOKING_KEY, JSON.stringify({ cid, aid }))
				// Card/3DS payment pages commonly refuse to load inside an iframe, so
				// break out to the top-level browsing context for this redirect.
				const target = window.top !== window.self ? window.top! : window
				target.location.href = data?.data?.authorization_url
			}
		} catch (err: any) {
			setLoading(false)
			setAwaitingPayment(false)
			toast.error(err?.response?.data?.message ?? 'Unable to start payment')
		}
	}

	const handleOtpSubmit = async () => {
		if (!otpInput || !payReference) return
		setSubmittingOtp(true)
		try {
			const { data } = await axiosClient.post('/payalt/verify-otp', {
				data: { otp: otpInput, reference: payReference },
				payPartner: 'paystack',
				patientId: Account?.uid,
			})
			if (data?.data?.paymentStatus === 'success' || data?.status === 'Success') {
				setShowOtp(false)
				setManualVerifying(true)
				setTimeLeft(300)
			} else {
				toast.error('OTP verification failed. Please try again.')
			}
		} catch (err: any) {
			toast.error(err?.response?.data?.message ?? 'OTP verification failed')
		} finally {
			setSubmittingOtp(false)
		}
	}

	const requestConsultationFunc = async () => {
		if (loading) return
		setLoading(true)
		requestConsultation(
			{
				type: 'G-H-C',
				source: BookingSource.BOOKING_PAGE,
				doctorData: {
					id: activeDoctor?.uid ?? activeDoctor.id,
					email: activeDoctor?.email,
					name: activeDoctor?.firstName + ' ' + activeDoctor?.lastName,
				},
				patientData: {
					id: Account?.uid || '',
					email: User?.email || '',
					name: User?.firstName + ' ' + User?.lastName,
					memberId: User?.memberID ?? '',
				},
				complaints: activeBooking.complaints,
				startTime: new Date(activeBooking?.startTime ?? 0),
				medium: activeBooking.visitType?.value,
				...(promoCodeData && { promoCodeId: promoCodeData?.id }),
				payment_method: selectedMethod?.type ?? 'WALLET',
			},
			async ({ status, data }: any) => {
				if (status === 'Success') {
					if (data?.requiresPayment && data?.paymentRequestId) {
						setPendingBooking({ cid: data.cid, aid: data.aid })
						await startPaymentForRequest(data.paymentRequestId, data.cid, data.aid)
					} else {
						setLoading(false)
						navigateToSuccess(data.cid, data.aid)
					}
					return
				}
				setLoading(false)
			}
		)
	}

	const continueFunc = () => {
		if (loading) return
		if (!hasFreePerks && paymentRemainder > 0 && !selectedMethod) {
			toast.error('Please select a payment method')
			return
		}
		if (selectedMethod?.type === 'mobile_money' && (!selectedMethod.value || selectedMethod.value.trim().length < 9)) {
			toast.error('Please enter a valid phone number')
			return
		}
		requestConsultationFunc()
	}

	if (!Account) return <></>

	// OTP screen
	if (awaitingPayment && showOtp) {
		return (
			<div className="flex flex-col flex-1 items-center justify-center p-6">
				<div className="bg-white rounded-2xl border border-gray-100 shadow p-6 w-full flex flex-col items-center gap-5">
					<div className="w-14 h-14 rounded-full bg-[#edfbf8] flex items-center justify-center">
						<Smartphone size={28} color="#034a47" />
					</div>
					<div className="text-center">
						<p className="text-base font-bold text-[#022B29] mb-1">Enter OTP</p>
						<p className="text-xs text-[#546E6C]">Enter the one-time password sent to your mobile money number.</p>
					</div>
					<input
						type="text"
						inputMode="numeric"
						maxLength={6}
						value={otpInput}
						onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, ''))}
						placeholder="• • • • • •"
						className="w-full text-center text-2xl font-bold tracking-[0.5em] border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#034a47]"
						autoFocus
					/>
					<div className="w-full flex flex-col gap-2">
						<button
							onClick={handleOtpSubmit}
							disabled={otpInput.length < 4 || submittingOtp}
							className="w-full py-3 bg-[#034a47] text-white rounded-full text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
						>
							{submittingOtp ? <RotatingLines width="16" strokeColor="#ffffff" /> : 'Verify OTP'}
						</button>
						<button
							onClick={() => { setShowOtp(false); setAwaitingPayment(false); setLoading(false) }}
							className="w-full py-3 border-2 border-gray-200 rounded-full text-sm font-semibold text-[#022B29]"
						>
							Cancel
						</button>
					</div>
				</div>
			</div>
		)
	}

	// Verifying countdown screen
	if (awaitingPayment && manualVerifying) {
		return (
			<div className="flex flex-col flex-1 items-center justify-center p-6">
				<div className="bg-white rounded-2xl border border-gray-100 shadow p-6 w-full flex flex-col items-center gap-5">
					<div className="relative w-20 h-20 flex items-center justify-center">
						<svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 80 80">
							<circle cx="40" cy="40" r="34" fill="none" stroke="#e4e2dd" strokeWidth="6" />
							<circle cx="40" cy="40" r="34" fill="none" stroke="#034a47" strokeWidth="6"
								strokeDasharray={`${2 * Math.PI * 34}`}
								strokeDashoffset={`${2 * Math.PI * 34 * (1 - timeLeft / 300)}`}
								strokeLinecap="round" className="transition-all duration-1000" />
						</svg>
						<span className="text-base font-bold text-[#022B29] font-mono">{formatTimer(timeLeft)}</span>
					</div>
					<div className="text-center">
						<p className="text-base font-bold text-[#022B29] mb-1">Verifying Payment…</p>
						<p className="text-xs text-[#546E6C]">Please wait while we confirm your payment.</p>
					</div>
					<div className="flex items-center gap-2 text-[#546E6C] text-xs">
						<RotatingLines width="16" strokeColor="#034a47" />
						<span>Listening for confirmation…</span>
					</div>
					<button
						onClick={() => { if (timerRef.current) clearInterval(timerRef.current); setManualVerifying(false); setAwaitingPayment(false); setLoading(false) }}
						className="text-xs text-[#546E6C] underline"
					>
						Cancel and go back
					</button>
				</div>
			</div>
		)
	}

	// Awaiting momo payment screen
	if (awaitingPayment) {
		return (
			<div className="flex flex-col flex-1 items-center justify-center p-6">
				<div className="bg-white rounded-2xl border border-gray-100 shadow p-6 w-full flex flex-col items-center gap-5">
					<div className="w-14 h-14 rounded-full bg-[#edfbf8] flex items-center justify-center">
						<Smartphone size={28} color="#034a47" />
					</div>
					<div className="text-center">
						<p className="text-base font-bold text-[#022B29] mb-1">Complete Payment on Your Phone</p>
						<p className="text-xs text-[#546E6C]">{paymentMessage}</p>
						{pendingPrId && (
							<a
								href={`https://app.drdogood.health/pay/${pendingPrId}`}
								target="_blank"
								rel="noopener noreferrer"
								className="text-xs text-[#034a47] underline mt-2 inline-block font-medium"
							>
								Open payment page →
							</a>
						)}
					</div>
					<div className="w-full flex flex-col gap-2">
						<button
							onClick={() => { setManualVerifying(true); setTimeLeft(300) }}
							className="w-full py-3 bg-[#034a47] text-white rounded-full text-sm font-semibold"
						>
							I&apos;ve Completed Payment
						</button>
						<button
							onClick={() => { setAwaitingPayment(false); setLoading(false) }}
							className="w-full py-3 border-2 border-gray-200 rounded-full text-sm font-semibold text-[#022B29]"
						>
							Cancel
						</button>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className="flex flex-col flex-1 space-y-4 overflow-y-scroll scroll">
			<div className="px-2">
				<p className="text-lg font-bold text-gray900">Confirm Your Booking</p>
				<p className="text-xs text-gray500">You&apos;ll receive a video call link on your phone after booking.</p>
			</div>

			<div className="mx-2 border-[#D6D6D6] bg-[#F6F6F6] border-opacity-20 flex flex-col h-28 px-4 py-2 rounded-md border">
				<p className="text-xs font-semibold text-gray700 mb-1">Reason for appointment:</p>
				<textarea
					value={activeBooking.complaints}
					onChange={(e) => dispatch(setActiveBooking({ ...activeBooking, complaints: e.target.value }))}
					placeholder="Describe your symptoms…"
					className="flex-1 w-full text-xs bg-transparent outline-none text-gray500"
				/>
			</div>

			{/* Payment methods */}
			<div className="flex-1 px-2">
				<p className="text-[10px] font-semibold text-gray700 mb-3">Select payment method</p>
				<div className="flex flex-col gap-3">

					{/* Wallet */}
					<button
						onClick={() => setSelectedMethod({ id: 'WALLET', type: 'WALLET' })}
						className={`flex items-center gap-3 p-3 border-2 rounded-xl transition-all ${selectedMethod?.type === 'WALLET' ? 'border-[#034a47] bg-[#f0faf9]' : 'border-gray-200'}`}
					>
						<div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${selectedMethod?.type === 'WALLET' ? 'bg-[#034a47]' : 'border-2 border-gray-300'}`}>
							{selectedMethod?.type === 'WALLET' && <Check size={10} color="#fff" />}
						</div>
						<div className="flex-1 text-left">
							<p className="text-xs font-semibold text-gray-800">DDG Wallet</p>
							<p className="text-[11px] text-gray-500">₵{walletBalance.toFixed(2)}</p>
						</div>
						<Image useBase alt="wallet" src="/img/empty-wallet.svg" width={16} height={16} />
					</button>

					{/* Mobile Money — expands with provider grid + phone */}
					{paymentRemainder > 0 && (
						<div className={`border-2 rounded-xl transition-all ${selectedMethod?.type === 'mobile_money' ? 'border-[#034a47]' : 'border-gray-200'}`}>
							<button
								onClick={() => { if (selectedMethod?.type !== 'mobile_money') setSelectedMethod({ id: '', type: 'mobile_money', value: momoPhone }) }}
								className="flex items-center gap-3 p-3 w-full"
							>
								<div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${selectedMethod?.type === 'mobile_money' ? 'bg-[#034a47]' : 'border-2 border-gray-300'}`}>
									{selectedMethod?.type === 'mobile_money' && <Check size={10} color="#fff" />}
								</div>
								<p className="text-xs font-semibold text-gray-800 flex-1 text-left">Mobile Money</p>
								<Image useBase width={60} height={16} alt="momo" src="/img/momo.svg" className="object-contain" />
							</button>
							{selectedMethod?.type === 'mobile_money' && (
								<div className="px-3 pb-3 flex flex-col gap-3">
									<div className="grid grid-cols-2 gap-2">
										{MOMO_PROVIDERS.map((p) => {
											const isSel = selectedMethod.id === p.id
											return (
												<button
													key={p.id}
													onClick={() => setSelectedMethod({ id: p.id, type: 'mobile_money', value: momoPhone })}
													className={`relative p-2.5 border-2 rounded-xl flex flex-col items-center gap-1.5 ${isSel ? 'border-[#034a47] bg-[#f0faf9]' : 'border-gray-200'}`}
												>
													{isSel && (
														<div className="absolute top-1.5 right-1.5 w-3.5 h-3.5 rounded-full bg-[#034a47] flex items-center justify-center">
															<Check size={8} color="#fff" />
														</div>
													)}
													<img src={p.logo} alt={p.label} className="h-5 w-full object-contain" />
													<span className="text-[10px] font-medium text-gray-600">{p.label}</span>
												</button>
											)
										})}
									</div>
									<div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 border-gray-200 focus-within:border-[#034a47]">
										<Phone size={13} color="#9CA3AF" className="flex-shrink-0" />
										<input
											type="tel"
											value={momoPhone}
											onChange={(e) => {
												setMomoPhone(e.target.value)
												if (selectedMethod?.type === 'mobile_money') setSelectedMethod({ ...selectedMethod, value: e.target.value })
											}}
											placeholder="0241234567"
											className="flex-1 text-xs text-gray-800 placeholder-gray-300 outline-none"
										/>
									</div>
								</div>
							)}
						</div>
					)}

					{/* Bank Card */}
					{paymentRemainder > 0 && (
						<button
							onClick={() => setSelectedMethod({ id: 'card', type: 'card' })}
							className={`flex items-center gap-3 p-3 border-2 rounded-xl transition-all ${selectedMethod?.id === 'card' ? 'border-[#034a47] bg-[#f0faf9]' : 'border-gray-200'}`}
						>
							<div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${selectedMethod?.id === 'card' ? 'bg-[#034a47]' : 'border-2 border-gray-300'}`}>
								{selectedMethod?.id === 'card' && <Check size={10} color="#fff" />}
							</div>
							<p className="text-xs font-semibold text-gray-800 flex-1 text-left">Bank Card</p>
							<Image useBase width={50} height={20} alt="cards" src="/img/cards.png" className="object-contain" />
						</button>
					)}

					{/* Saved cards collapsible */}
					{PaymentMethods.length > 0 && (
						<div>
							<button onClick={() => setSavedCardsOpen((o) => !o)} className="flex items-center gap-2 text-xs text-[#034a47] font-semibold py-1">
								<CreditCard size={13} />
								{savedCardsOpen ? 'Hide' : 'Show'} saved cards ({PaymentMethods.length})
							</button>
							{savedCardsOpen && (
								<div className="flex flex-col gap-2 mt-2">
									{PaymentMethods.map((method) => {
										const isSel = selectedMethod?.id === method.id
										return (
											<button
												key={method.id}
												onClick={() => setSelectedMethod({ id: method.id, type: 'card' })}
												className={`flex items-center gap-3 p-3 border-2 rounded-xl ${isSel ? 'border-[#034a47] bg-[#f0faf9]' : 'border-gray-200'}`}
											>
												<div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${isSel ? 'bg-[#034a47]' : 'border-2 border-gray-300'}`}>
													{isSel && <Check size={10} color="#fff" />}
												</div>
												<CreditCard size={13} color="#9CA3AF" className="flex-shrink-0" />
												<div className="flex-1 text-left">
													<p className="text-xs font-medium text-gray-800">•••• {method.last4}</p>
													<p className="text-[10px] text-gray-400 capitalize">{method.card_type} · {method.exp_month}/{method.exp_year}</p>
												</div>
											</button>
										)
									})}
								</div>
							)}
						</div>
					)}
				</div>
			</div>

			{/* Promo code */}
			{!activeDoctor?.accepts_perks && (
				<div className="px-2 border-t border-gray-100 pt-3">
					<div className="flex items-center gap-3">
						<Tag size={12} fill={DDGColors.primary} color="#034a47" />
						<input
							value={promoCode ?? ''}
							placeholder="Got a promo code?"
							onChange={(e) => setPromoCode(e.target.value)}
							disabled={Boolean(promoCodeData)}
							className="flex-1 text-sm outline-none"
						/>
						{promoCode && !promoCodeData && (
							<button onClick={() => setPromoCode('')} className="p-1 bg-gray-200 rounded-full"><X size={12} /></button>
						)}
						{promoCode && promoCode.length >= 4 && !promoCodeData && (
							<button
								onClick={() => {
									setApplying(true)
									applyPromo(promoCode.trim(), async (val: any) => {
										setApplying(false)
										val.status === 'FAILED' ? toast.error(val.message) : toast.success(val.message)
										if (val.promoCodeData) setPromoCodeData(val.promoCodeData)
										if (val.status !== 'FAILED') fetchData()
									})
								}}
								disabled={applying || loading}
							>
								{applying ? <TailSpin width={16} height={16} color={DDGColors.primary} /> : <span className="text-xs font-bold text-primary">Apply</span>}
							</button>
						)}
						{promoCodeData && (
							<button className="p-1" onClick={() => setPromoCodeData(undefined)}>
								<Trash size={14} color={DDGColors.blood} />
							</button>
						)}
					</div>
					{promoCodeData && <p className="text-xs text-gray-500 mt-1">{promoCodeData.description}</p>}
				</div>
			)}

			{/* Summary + confirm */}
			<div className="px-2 pt-2 w-full border-t-2 border-gray-50">
				<div className="flex flex-row items-end justify-between">
					<div className="w-1/2">
						{hasFreePerks ? (
							<p className="font-bold text-md text-primary">Free Visit</p>
						) : (
							<p className="text-sm font-bold text-primary">₵{effectiveTotal.toFixed(2)}</p>
						)}
						<p className="text-xs font-bold">{convertDateToString(new Date(activeBooking.startTime ?? 0), true)}</p>
						<p className="text-[10px] text-gray-400 font-light">Free cancellation*</p>
					</div>
					<div className="w-1/2 pl-3">
						<button
							className="flex items-center justify-center w-full py-3 rounded-md bg-primary"
							onClick={() => {
								if (activeBooking?.startTime && new Date(activeBooking.startTime) < new Date()) {
									toast.error('Appointment time elapsed, select a new time')
									return
								}
								if (promoCode && !promoCodeData) {
									if (!window.confirm('You have an unapplied promo code. Continue?')) return
								}
								continueFunc()
							}}
						>
							{loading ? <RotatingLines width="20" strokeColor={DDGColors.gainsboro} /> : <p className="text-xs font-semibold text-white">Continue</p>}
						</button>
					</div>
				</div>
			</div>
		</div>
	)
}

export default StepThree
